// useIdleTimeout — operator-only idle-timeout hook (plan madde 2.8 +
// spec 60 VITE_OPERATOR_SESSION_IDLE_TIMEOUT_SEC; Keycloak realm ceiling
// remains 1800s).
//
// Operator console session is shorter than customer-web (60 min) per
// spec 60 §"elevated operation": operators have access to cross-tenant
// data + impersonation, so an unattended laptop is a higher blast-
// radius risk. After `idleSec` of no user activity (configured event
// list — pointer, keyboard, touch, scroll, focus), the hook fires
// `onIdle()` once. The caller (IdleTimeoutGuard) wires that to the
// OIDC signoutRedirect.
//
// Activity events deliberately omit `mousemove`/`pointermove` — those
// fire on every cursor wiggle and effectively defeat the timeout.
// Instead we sample on intentful actions (click/key/touch/scroll/focus).
//
// Phase 1 scaffold: no warning toast at idle - 60s; the guard simply
// signs the operator out at the deadline. Plan madde 11 layers a
// 60-second-warning UI with a "Stay signed in" button on top of this
// hook.

import { useEffect, useRef } from "react";

const DEFAULT_ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "focus",
] as const;

export interface UseIdleTimeoutOptions {
  /** Idle threshold in seconds. */
  readonly idleSec: number;
  /** Called once when the threshold is crossed without activity. */
  readonly onIdle: () => void;
  /** When false, the hook is dormant (no listeners, no timer). */
  readonly enabled?: boolean;
  /** Override the activity event set. Defaults to intentful actions. */
  readonly events?: readonly string[];
}

export function useIdleTimeout(opts: UseIdleTimeoutOptions): void {
  const { idleSec, onIdle, enabled = true, events = DEFAULT_ACTIVITY_EVENTS } = opts;
  // Refs let the parent re-render without forcing a teardown of the listener
  // set. `onIdle` and `events` may both be fresh references each render
  // (literal arrow functions, inline array literals); without refs the
  // effect would cleanup + remount on every parent render — clearing the
  // running timer + reinstalling listeners — and any cursor-driven re-render
  // from upstream Zustand/Query would silently reset the countdown.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect((): (() => void) | undefined => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;

    // Snapshot events at effect-mount time. The hook contract assumes a
    // stable event SET per session — runtime swap of the events array
    // (e.g., adding "click" mid-flight) is out-of-scope; if a future
    // caller needs that, the API should grow an explicit reset/remount
    // signal rather than re-running the effect on every parent render.
    const eventList = eventsRef.current;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let fired = false;

    const fire = (): void => {
      if (fired) return;
      fired = true;
      onIdleRef.current();
    };

    const reset = (): void => {
      if (fired) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, idleSec * 1000);
    };

    for (const event of eventList) {
      window.addEventListener(event, reset, { passive: true });
    }
    reset(); // initial timer

    return (): void => {
      if (timer !== null) clearTimeout(timer);
      for (const event of eventList) {
        window.removeEventListener(event, reset);
      }
    };
    // events intentionally NOT in deps — see eventsRef snapshot rationale above.
  }, [enabled, idleSec]);
}
